import { ArchitectureTemplate, ArchitectureNode, ArchitectureEdge } from '../types';
import { generateThumbnail } from './imageExport';

const STORAGE_KEY = 'archweaver_user_templates';

// Generate unique ID
const generateId = (): string => {
  return `user_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
};

// Get all user templates from localStorage
export const getUserTemplates = (): ArchitectureTemplate[] => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return [];
    return JSON.parse(stored);
  } catch (error) {
    console.error('Failed to get user templates:', error);
    return [];
  }
};

// Save a user template
export const saveUserTemplate = async (
  name: string,
  description: string,
  category: 'web' | 'microservices' | 'serverless' | 'data' | 'kubernetes',
  difficulty: 'beginner' | 'intermediate' | 'advanced',
  nodes: ArchitectureNode[],
  edges: ArchitectureEdge[],
  canvasElement?: HTMLElement
): Promise<ArchitectureTemplate> => {
  const templates = getUserTemplates();
  
  // Generate thumbnail if canvas element provided
  let thumbnail = '';
  if (canvasElement) {
    try {
      thumbnail = await generateThumbnail(canvasElement);
    } catch (error) {
      console.error('Failed to generate thumbnail:', error);
    }
  }
  
  const template: ArchitectureTemplate = {
    id: generateId(),
    name: name || 'Untitled Template',
    description: description || '',
    category,
    difficulty,
    nodes,
    edges,
    thumbnail
  };
  
  templates.push(template);
  localStorage.setItem(STORAGE_KEY, JSON.stringify(templates));
  
  return template;
};

// Delete a user template
export const deleteUserTemplate = (id: string): boolean => {
  try {
    const templates = getUserTemplates();
    const filtered = templates.filter(t => t.id !== id);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filtered));
    return true;
  } catch (error) {
    console.error('Failed to delete template:', error);
    return false;
  }
};

// Update a user template
export const updateUserTemplate = (
  id: string,
  updates: Partial<ArchitectureTemplate>
): ArchitectureTemplate | null => {
  try {
    const templates = getUserTemplates();
    const index = templates.findIndex(t => t.id === id);
    if (index === -1) return null;
    
    templates[index] = { ...templates[index], ...updates };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(templates));
    return templates[index];
  } catch (error) {
    console.error('Failed to update template:', error);
    return null;
  }
};

// Export template as JSON
export const exportTemplateAsJSON = (template: ArchitectureTemplate): string => {
  return JSON.stringify(template, null, 2);
};

// Import template from JSON
export const importTemplateFromJSON = (jsonString: string): ArchitectureTemplate | null => {
  try {
    const template = JSON.parse(jsonString) as ArchitectureTemplate;
    
    // Validate template structure
    if (!template.nodes || !template.edges || !template.name) {
      throw new Error('Invalid template structure');
    }
    
    // Assign new ID to avoid conflicts
    const newTemplate = {
      ...template,
      id: generateId()
    };
    
    const templates = getUserTemplates();
    templates.push(newTemplate);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(templates));
    
    return newTemplate;
  } catch (error) {
    console.error('Failed to import template:', error);
    return null;
  }
};
